import {
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
  useIsMarkdownCodeBlock,
} from "@assistant-ui/react-markdown";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { memo, createContext, useContext, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { cn } from "../../lib/utils";
import { attachmentUrl } from "../../lib/attachments";

// MarkdownText that uses assistant-ui context (for normal message rendering)
const MarkdownTextImpl = () => {
  return (
    <MarkdownTextPrimitive
      remarkPlugins={[remarkGfm]}
      className="aui-md"
      components={defaultComponents}
    />
  );
};

export const MarkdownText = memo(MarkdownTextImpl);

// StandaloneMarkdown that accepts a text prop (for filtered/processed text)
const StandaloneMarkdownImpl = ({ text }) => {
  if (!text) return null;
  return (
    <div className="aui-md">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={markdownComponents}
      >
        {text}
      </Markdown>
    </div>
  );
};

export const StandaloneMarkdown = memo(StandaloneMarkdownImpl);

export const TableExportEnabledContext = createContext(true);

// Shared styling for tables (MUI sx). Wide product catalogs would overflow the
// chat area without the wrapper, and without word-break long cell text stretches
// the layout sideways.
const tableWrapSx = {
  width: '100%',
  overflowX: 'auto',
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
};
const tableSx = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.875rem',
};
const thSx = {
  borderBottom: 1,
  borderColor: 'divider',
  bgcolor: 'action.hover',
  px: 1.5,
  py: 1,
  textAlign: 'left',
  fontWeight: 600,
  verticalAlign: 'top',
  wordBreak: 'break-word',
};
const tdSx = {
  borderBottom: 1,
  borderColor: 'divider',
  px: 1.5,
  py: 1,
  verticalAlign: 'top',
  wordBreak: 'break-word',
};

// react-markdown passes a `node` prop (the AST node). We strip it so it doesn't
// land on the DOM as an unknown `node="[object Object]"` attribute.
const stripNode = ({ node, ...rest }) => rest;

const cellText = (node) => {
  if (!node) return '';
  if (node.type === 'text') return node.value;
  if (node.type === 'element' && node.tagName === 'br') return ' ';
  if (Array.isArray(node.children)) return node.children.map(cellText).join('');
  return '';
};

const extractTableData = (node) => {
  if (!node || node.type !== 'element' || node.tagName !== 'table') return null;
  const rows = [];
  const walk = (el) => {
    for (const child of el.children || []) {
      if (!child || child.type !== 'element') continue;
      if (child.tagName === 'tr') {
        const cells = (child.children || [])
          .filter((c) => c.type === 'element' && (c.tagName === 'td' || c.tagName === 'th'))
          .map(cellText);
        if (cells.length > 0) rows.push(cells);
      } else {
        walk(child);
      }
    }
  };
  walk(node);
  if (rows.length === 0) return null;
  return { headers: rows[0], rows: rows.slice(1) };
};

const csvEscape = (value) => {
  const s = value == null ? '' : String(value);
  return /["\n\r,]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
};

const tableToCsv = (data) => {
  const lines = [data.headers, ...data.rows].map((row) => row.map(csvEscape).join(','));
  return '\uFEFF' + lines.join('\r\n');
};

const timestampedFilename = (ext) => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, '0');
  return `table_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.${ext}`;
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const exportButtonSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'background.paper',
  color: 'text.secondary',
  fontFamily: 'inherit',
  fontSize: '0.7rem',
  fontWeight: 700,
  lineHeight: 1,
  px: 1,
  py: 0.5,
  cursor: 'pointer',
  '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
  '&:disabled': { cursor: 'default', opacity: 0.5 },
};

const TableExportToolbar = ({ data }) => {
  const [busy, setBusy] = useState(null);

  const handleCsv = () => {
    setBusy('csv');
    try {
      triggerDownload(
        new Blob([tableToCsv(data)], { type: 'text/csv;charset=utf-8' }),
        timestampedFilename('csv')
      );
    } catch (err) {
      console.error('Table CSV export failed:', err);
    } finally {
      setBusy(null);
    }
  };

  const handleXlsx = async () => {
    setBusy('xlsx');
    try {
      const XLSX = await import('xlsx');
      const ws = XLSX.utils.aoa_to_sheet([data.headers, ...data.rows]);
      ws['!cols'] = data.headers.map((h, c) => {
        let max = String(h == null ? '' : h).length;
        for (const row of data.rows) {
          const len = String(row[c] == null ? '' : row[c]).length;
          if (len > max) max = len;
        }
        return { wch: Math.min(Math.max(max + 2, 8), 50) };
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Data');
      const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      triggerDownload(
        new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        timestampedFilename('xlsx')
      );
    } catch (err) {
      console.error('Table XLSX export failed:', err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 0.5,
        mb: 0.5,
        opacity: 0.6,
        transition: 'opacity 0.15s ease',
        '&:hover': { opacity: 1 },
      }}
    >
      <Tooltip title="Export as CSV" arrow>
        <Box component="button" sx={exportButtonSx} onClick={handleCsv} disabled={busy !== null}>
          CSV
        </Box>
      </Tooltip>
      <Tooltip title="Export as Excel" arrow>
        <Box component="button" sx={exportButtonSx} onClick={handleXlsx} disabled={busy !== null}>
          XLSX
        </Box>
      </Tooltip>
    </Box>
  );
};

const MarkdownTable = ({ node, ...rest }) => {
  const exportEnabled = useContext(TableExportEnabledContext);
  const data = exportEnabled ? extractTableData(node) : null;
  return (
    <Box component="div" sx={{ my: 2 }}>
      {data ? <TableExportToolbar data={data} /> : null}
      <Box component="div" sx={tableWrapSx}>
        <Box component="table" sx={tableSx} {...rest} />
      </Box>
    </Box>
  );
};

// Images in chat markdown. `![alt](attachment:file_xxx)` — the reference
// syntax the media architecture uses in exports — also works in CHAT: we
// rewrite it to the same /attachments endpoint history images already use.
// Empty/garbage srcs (weak models emit them) render as the alt text instead
// of a broken-image icon.
const resolveImgSrc = (src) => {
  if (!src) return null;
  if (src.startsWith("attachment:")) {
    const id = src.slice("attachment:".length).trim();
    return id ? attachmentUrl(id) : null;
  }
  if (src.startsWith("data:image/") || src.startsWith("http://") || src.startsWith("https://") || src.startsWith("/")) {
    return src;
  }
  return null; // unknown scheme → don't render a broken image
};

const ImgRenderer = ({ node, src, alt, ...props }) => {
  const resolved = resolveImgSrc(src);
  if (!resolved) {
    return alt ? <em style={{ fontSize: "0.85em", opacity: 0.7 }}>[{alt}]</em> : null;
  }
  return (
    <img
      src={resolved}
      alt={alt || ""}
      loading="lazy"
      style={{ maxWidth: "100%", borderRadius: 8, margin: "8px 0", display: "block" }}
      {...props}
    />
  );
};

// Links: `[name](attachment:file_xxx)` — same reference syntax as images —
// resolve to the /attachments endpoint so the model can hand the user a
// working open/download link for any materialized file.
const LinkRenderer = ({ node, href, children, ...props }) => {
  let resolved = href;
  if (href && href.startsWith("attachment:")) {
    const id = href.slice("attachment:".length).trim();
    resolved = id ? attachmentUrl(id) : undefined;
  }
  if (!resolved) {
    return <>{children}</>;
  }
  return (
    <a href={resolved} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
};

// Components for react-markdown (slightly different API than assistant-ui).
// Exported so other components rendering Markdown directly (e.g. history
// playback in thread.jsx) can apply the same table/code styling.
export const markdownComponents = {
  a: LinkRenderer,
  img: ImgRenderer,
  p: ({ node, className, ...props }) => (
    <p
      className={cn(
        "aui-md-p",
        className,
      )}
      style={{ marginTop: '1rem', marginBottom: '1rem', lineHeight: 1.7 }}
      {...props}
    />
  ),
  code: ({ node, className, inline, ...props }) => (
    <code
      className={cn(inline && "aui-md-inline-code", className)}
      style={inline ? {
        background: 'rgba(0,0,0,0.06)',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 4,
        padding: '0 4px',
        fontWeight: 600,
      } : undefined}
      {...props}
    />
  ),
  table: MarkdownTable,
  thead: (props) => <thead {...stripNode(props)} />,
  tbody: (props) => <tbody {...stripNode(props)} />,
  tr: (props) => <tr {...stripNode(props)} />,
  th: (props) => <Box component="th" sx={thSx} {...stripNode(props)} />,
  td: (props) => <Box component="td" sx={tdSx} {...stripNode(props)} />,
};

const defaultComponents = memoizeMarkdownComponents({
  a: LinkRenderer,
  p: ({ node, className, ...props }) => (
    <p
      className={cn("aui-md-p", className)}
      style={{ marginTop: '1rem', marginBottom: '1rem', lineHeight: 1.7 }}
      {...props}
    />
  ),
  code: function Code({ node, className, ...props }) {
    const isCodeBlock = useIsMarkdownCodeBlock();
    return (
      <code
        className={cn(!isCodeBlock && "aui-md-inline-code", className)}
        style={!isCodeBlock ? {
          background: 'rgba(0,0,0,0.06)',
          border: '1px solid rgba(0,0,0,0.12)',
          borderRadius: 4,
          padding: '0 4px',
          fontWeight: 600,
        } : undefined}
        {...props}
      />
    );
  },
  table: MarkdownTable,
  thead: (props) => <thead {...stripNode(props)} />,
  tbody: (props) => <tbody {...stripNode(props)} />,
  tr: (props) => <tr {...stripNode(props)} />,
  th: (props) => <Box component="th" sx={thSx} {...stripNode(props)} />,
  td: (props) => <Box component="td" sx={tdSx} {...stripNode(props)} />,
});
